import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

const COLORS = {
  completed: '#408E0F',
  current: 'linear-gradient(135deg, #3B173F, #073270)',
  upcoming: '#FF7600',
  future: '#D9D9D9',
  futureBorder: '#D9D9D9',
  requirementFulfilled: '#2196F3',
  requirementPending: '#FF7600',
};

const styles = () => ({
  progressBarWrapper: {
    position: 'relative',
    width: '100%',
  },
  progressBarContainer: {
    display: 'flex',
    // alignItems: 'center',
    width: '100%',
    padding: '8px 0',
    // overflowX: 'auto',
    // marginTop: '-86px',
    maxWidth: '600px',
  },
  statusNode: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
    position: 'relative',
  },
  circleBase: {
    borderRadius: '50%',
    flexShrink: 0,
    position: 'relative',
    zIndex: 1,
    marginTop: '-7px'
  },
  circleCompleted: {
    width: 14,
    height: 14,
    backgroundColor: COLORS.completed,
  },
  circleCurrent: {
    width: 16,
    height: 16,
    background: COLORS.current,
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.2)',
  },
  circleUpcoming: {
    width: 14,
    height: 14,
    backgroundColor: COLORS.upcoming,
  },
  circleFuture: {
    width: 14,
    height: 14,
    backgroundColor: COLORS.future,
  },
  connector: {
    flex: '1 1 0',
    height: 2,
    // minWidth: 16,
    // maxWidth: 16,
    backgroundColor: COLORS.future,
    position: 'relative',
    zIndex: 1,
  },
  connectorCompleted: {
    backgroundColor: COLORS.completed,
  },
  requirementsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '0 8px',
    paddingTop: '12px',
    flexShrink: 0,
    position: 'relative',
    zIndex: 1,
    maxWidth: '100px',
    listStyle: 'decimal',
    paddingLeft: '24px',
    margin: 0,
  },
  requirementText: {
    fontSize: '0.75rem',
    lineHeight: 1.5,
    whiteSpace: 'normal',
    paddingLeft: '4px',
    fontFamily: 'Roboto, sans-serif',
  },
  requirementFulfilled: {
    color: COLORS.requirementFulfilled,
  },
  requirementPending: {
    color: COLORS.requirementPending,
  },
  tooltipText: {
    fontSize: '0.7rem',
    fontFamily: 'Roboto, sans-serif',
  },
  tooltipBottom: {
    backgroundColor: '#FAFAFA',
    fontSize: '0.75rem',
    padding: '6px 12px',
    borderRadius: '4px',
    color: '#333',
    border: '1px solid #000',
    position: 'absolute',
    top: '28px',
    whiteSpace: 'nowrap',
    zIndex: 10,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-6px',
      width: 0,
      height: 0,
      borderLeft: '6px solid transparent',
      borderRight: '6px solid transparent',
      borderBottom: '6px solid #000',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '-5px',
      width: 0,
      height: 0,
      borderLeft: '6px solid transparent',
      borderRight: '6px solid transparent',
      borderBottom: '6px solid #FAFAFA',
    },
  },
  tooltipCurrent: {
    right: '-5px',
    maxWidth: '250px',
    whiteSpace: 'normal',
    '&::before': {
      right: '4px',
    },
    '&::after': {
      right: '4px',
    },
  },
  tooltipUpcoming: {
    left: '-5px',
    // transform: 'translateX(-50%)',
    '&::before': {
      left: '4px',
      // marginLeft: '-6px',
    },
    '&::after': {
      left: '4px',
      // marginLeft: '-6px',
    },
  },
  tooltipArrowBottom: {
    color: '#FAFAFA',
    '&::before': {
      border: '1px solid #000',
    },
  },
  tooltipTop: {
    backgroundColor: '#FFFEF0',
    fontSize: '0.75rem',
    padding: '6px 12px',
    borderRadius: '4px',
    color: '#333',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  },
  tooltipArrowTop: {
    color: '#FFFEF0',
  },
  separatorLine: {
    position: 'absolute',
    width: '600px',
    height: '2px',
    backgroundColor: '#D9D9D9',
    top: '9px',
    left: 0,
    transform: 'translateY(-50%)',
    zIndex: 0,
    pointerEvents: 'none',
  },
});

function BatchProgressBar({ progressBar, classes }) {
  if (!progressBar || !progressBar.statusSequence) {
    return null;
  }

  const { statusSequence, pendingRequirements } = progressBar;

  // Sort statuses by sequence number to ensure correct order
  const sortedStatusSequence = [...statusSequence].sort((a, b) => 
    (a.sequence || 0) - (b.sequence || 0)
  );

  const getCircleClass = (state) => {
    const base = classes.circleBase;
    switch (state) {
      case 'completed':
        return `${base} ${classes.circleCompleted}`;
      case 'current':
        return `${base} ${classes.circleCurrent}`;
      case 'upcoming':
        return `${base} ${classes.circleUpcoming}`;
      default:
        return `${base} ${classes.circleFuture}`;
    }
  };

  const getTooltipContent = (status, state) => {
    if (state === 'current') {
      return `${status.statusName || status.statusDisplayName}`;
    }
    if (state === 'upcoming') {
      return `${status.statusName || status.statusDisplayName}`;
    }
    return status.statusName || status.statusDisplayName;
  };

  // Find the index of the current status to know where to insert requirements
  const currentIndex = sortedStatusSequence.findIndex((s) => s.state === 'current');
  
  // Determine upcoming status: prefer backend state, fallback to next item after current
  const upcomingIndex = sortedStatusSequence.findIndex((s) => s.state === 'upcoming');
  const calculatedUpcomingIndex = upcomingIndex === -1 && currentIndex !== -1 
    ? currentIndex + 1 
    : upcomingIndex;

  return (
    <div className={classes.progressBarWrapper}>
      <div className={classes.separatorLine} />
      <div className={classes.progressBarContainer}>
        {sortedStatusSequence.map((status, index) => {
        const isBeforeCurrent =
          index < currentIndex || (currentIndex === -1 && false);
        
        // Determine effective state: trust backend or use calculated upcoming index
        const effectiveState = index === calculatedUpcomingIndex ? 'upcoming' : status.state;

        return (
          <React.Fragment key={status.statusID}>
            {/* Connector line before this node (except first) */}
            {index > 0 && (
              <div
                className={`${classes.connector} ${
                  isBeforeCurrent || index === currentIndex
                    ? classes.connectorCompleted
                    : ''
                }`}
              />
            )}

            {/* Requirements indicator (between current and upcoming) */}
            {index === currentIndex + 1 &&
              pendingRequirements &&
              pendingRequirements.length > 0 && (
                <React.Fragment>
                  <ol className={classes.requirementsContainer}>
                    {pendingRequirements.map((req, reqIndex) => (
                      <li
                        key={reqIndex}
                        className={`${classes.requirementText} ${
                          req.fulfilled
                            ? classes.requirementFulfilled
                            : classes.requirementPending
                        }`}
                      >
                        {req.label}
                      </li>
                    ))}
                  </ol>
                  <div className={classes.connector} />
                </React.Fragment>
              )}

            {/* Status circle with tooltip */}
            <div className={classes.statusNode}>
              {effectiveState === 'current' || effectiveState === 'upcoming' ? (
                <>
                  <div className={getCircleClass(effectiveState)} />
                  {effectiveState === 'current' && (
                    <div 
                      className={`${classes.tooltipBottom} ${classes.tooltipCurrent}`}
                      style={{
                        minWidth: (status.statusName || status.statusDisplayName || '').length > 25 ? '190px' : undefined
                      }}
                    >
                      <span className={classes.tooltipText}>
                        {getTooltipContent(status, effectiveState)}
                      </span>
                    </div>
                  )}
                  {effectiveState === 'upcoming' && (
                    <div className={`${classes.tooltipBottom} ${classes.tooltipUpcoming}`}>
                      <span className={classes.tooltipText}>
                        {getTooltipContent(status, effectiveState)}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <Tooltip
                  title={
                    <span className={classes.tooltipText}>
                      {getTooltipContent(status, effectiveState)}
                    </span>
                  }
                  placement="top"
                  arrow
                  classes={{
                    tooltip: classes.tooltipTop,
                    arrow: classes.tooltipArrowTop,
                  }}
                >
                  <div className={getCircleClass(effectiveState)} />
                </Tooltip>
              )}
            </div>
          </React.Fragment>
        );
      })}
      </div>
    </div>
  );
}

export default withStyles(styles)(BatchProgressBar);
